import { LengthUnit, MassUnit, EnergyUnit, VolumeUnit } from './enums';

export class MeasureUnitConfig {
  id: string;
  // English canonical description, shown as the help tooltip and label fallback in the unit
  // picker (cs-select-popover). Kept as the source-of-truth English word; the translated string
  // the user actually sees is resolved from descriptionKey by translateMeasureDescription in
  // client/src/util/formatting.ts.
  description: string;
  // stable translation key for `description`, used by translateMeasureDescription to look up the
  // localized text (df-b0427dd3fe59). Keyed separately from `id` on purpose: several configs
  // share an id with DIFFERENT descriptions (kcal/kJ), and Config_None has no id at all, so an
  // id-keyed lookup would collide. A config with no descriptionKey falls back to raw English.
  descriptionKey?: string;
  unit1: string;
  value1: number; // how many of the nominal units is 1 of this unit?
  unit2?: string;
  value2?: number; // how many of the nominal units is 1 of this unit?
  // note that from the relationship between value1 and value2, we can derive how many of unit2 is 1 of unit1

  // list of locales that prefer this config
  locales?: string[];
  // if set, try to use fractions, unless denominator would be too "ugly"
  fractionDenominatorMax?: number;
  // if true, always show the unit even when there's only one unit option (e.g., for skinfolds which are always in mm)
  alwaysShowUnit?: boolean;
  // sizing for unit1 input: XXS ~ 1 digit, XS ~ 2 digits, S ~ 3 digits, M ~ 5 digits, L ~ 7 digits
  valueLen?: 'XXS' | 'XS' | 'S' | 'M' | 'L';
  // sizing for unit2 input (dual-unit configs only)
  value2Len?: 'XXS' | 'XS' | 'S' | 'M' | 'L';
  // if true, unit1 is shown whole in the popup button/keyboard label, never run through
  // shortUnitName's word-truncation. Set this when unit1 is a TRANSLATED catalog value whose
  // length is controlled by the translation (e.g. a translated food-measure name, which can be
  // multiple words in some locales) -- shortUnitName's "first word + ellipsis" truncation is
  // meant for long free-text names, not for catalog text.
  doNotShortenUnit1?: boolean;
}

export const Config_None: MeasureUnitConfig = {
  id: undefined,
  description: 'none',
  descriptionKey: 'none',
  unit1: '',
  value1: undefined,
};

export const Config_Number: MeasureUnitConfig = {
  id: 'number',
  description: 'Generic number',
  descriptionKey: 'genericNumber',
  unit1: '',
  value1: 1,
};

export const Config_Fraction: MeasureUnitConfig = {
  id: 'number',
  description: 'Generic number',
  descriptionKey: 'genericNumber',
  unit1: '',
  value1: 1,
  fractionDenominatorMax: 8,
};

export const Config_Reps: MeasureUnitConfig = {
  id: 'reps',
  description: 'Repetitions',
  descriptionKey: 'repetitions',
  unit1: 'reps',
  value1: 1,
};

export const Config_Sets: MeasureUnitConfig = {
  id: 'sets',
  description: 'Sets',
  descriptionKey: 'sets',
  unit1: 'sets',
  value1: 1,
};

export const Config_Pairs: MeasureUnitConfig = {
  id: 'pairs',
  description: 'Pairs',
  descriptionKey: 'pairs',
  unit1: 'pairs',
  value1: 1,
};

export const Config_Plates: MeasureUnitConfig = {
  id: 'plates',
  description: 'Plates',
  descriptionKey: 'plates',
  unit1: 'plates',
  value1: 1,
};

export const Config_Pc: MeasureUnitConfig = {
  id: 'pc',
  description: 'Percent',
  descriptionKey: 'percent',
  unit1: '%',
  value1: 1,
};

export const Config_Millimeters: MeasureUnitConfig = {
  id: 'mm',
  description: 'millimeters',
  descriptionKey: 'millimeters',
  unit1: 'mm',
  value1: LengthUnit.mm,
  alwaysShowUnit: true,
};

export const Config_Centimeters: MeasureUnitConfig = {
  id: 'cm',
  description: 'centimeters',
  descriptionKey: 'centimeters',
  unit1: 'cm',
  value1: LengthUnit.cm,
};

export const Config_FeetInches: MeasureUnitConfig = {
  id: 'ft_in',
  description: 'feet and inches',
  descriptionKey: 'feetAndInches',
  unit1: 'ft',
  value1: LengthUnit.ft,
  unit2: 'in',
  value2: LengthUnit.in,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
  valueLen: 'XS',   // feet: 4-7 (1-2 digits)
  value2Len: 'XS',  // inches: 0-11 (1-2 digits)
};

export const Config_Inches: MeasureUnitConfig = {
  id: 'in',
  description: 'inches',
  descriptionKey: 'inches',
  unit1: 'in',
  value1: LengthUnit.in,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
};

export const Config_Kilograms: MeasureUnitConfig = {
  id: 'kg',
  description: 'kilograms',
  descriptionKey: 'kilograms',
  unit1: 'kg',
  value1: MassUnit.kg,
};

export const Config_Pounds: MeasureUnitConfig = {
  id: 'lb',
  description: 'pounds',
  descriptionKey: 'pounds',
  unit1: 'lb',
  value1: MassUnit.lb,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
};

export const Config_Micrograms: MeasureUnitConfig = {
  id: 'ug',
  description: 'micrograms',
  descriptionKey: 'micrograms',
  unit1: 'µg',
  value1: MassUnit.ug,
};

export const Config_Milligrams: MeasureUnitConfig = {
  id: 'mg',
  description: 'milligrams',
  descriptionKey: 'milligrams',
  unit1: 'mg',
  value1: MassUnit.mg,
};

export const Config_Grams: MeasureUnitConfig = {
  id: 'g',
  description: 'grams',
  descriptionKey: 'grams',
  unit1: 'g',
  value1: MassUnit.g,
};

export const Config_Ounces: MeasureUnitConfig = {
  id: 'oz',
  description: 'ounces',
  descriptionKey: 'ounces',
  unit1: 'oz',
  value1: MassUnit.oz,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
};

export const Config_StonesPounds: MeasureUnitConfig = {
  id: 'st_lb',
  description: 'stones and pounds',
  descriptionKey: 'stonesAndPounds',
  unit1: 'st',
  value1: MassUnit.st,
  unit2: 'lb',
  value2: MassUnit.lb,
  locales: ['GB', 'IE'],
  valueLen: 'XS',   // stones: 8-30 (1-2 digits)
  value2Len: 'XS',  // pounds: 0-13 (1-2 digits)
};

export const Config_Kcals: MeasureUnitConfig = {
  id: 'kcal',
  description: 'kilocalories',
  descriptionKey: 'kilocalories',
  unit1: 'kcal',
  value1: EnergyUnit.kcal,
};

export const Config_Kjoules: MeasureUnitConfig = {
  id: 'kcal',
  description: 'kilojoules',
  descriptionKey: 'kilojoules',
  unit1: 'kJ',
  value1: EnergyUnit.kJ,
};

export const Config_IU: MeasureUnitConfig = {
  id: 'IU',
  description: 'international units',
  descriptionKey: 'internationalUnits',
  unit1: 'IU',
  value1: 1,
};

export const Config_Milliliters: MeasureUnitConfig = {
  id: 'ml',
  description: 'milliliters',
  descriptionKey: 'milliliters',
  unit1: 'ml',
  value1: VolumeUnit.ml,
};

export const Config_FluidOunces: MeasureUnitConfig = {
  id: 'floz',
  description: 'fluid ounces',
  descriptionKey: 'fluidOunces',
  unit1: 'fl oz',
  value1: VolumeUnit.fl_oz,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
};

export const Config_Quarts: MeasureUnitConfig = {
  id: 'qt',
  description: 'quarts',
  descriptionKey: 'quarts',
  unit1: 'qt',
  value1: VolumeUnit.qt,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
};

export const Config_Pints: MeasureUnitConfig = {
  id: 'pt',
  description: 'pints',
  descriptionKey: 'pints',
  unit1: 'pt',
  value1: VolumeUnit.pt,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
};

export const Config_Cups: MeasureUnitConfig = {
  id: 'cup',
  description: 'cups',
  descriptionKey: 'cups',
  unit1: 'cup',
  value1: VolumeUnit.cup,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
};

export const Config_Tablespoons: MeasureUnitConfig = {
  id: 'tbsp',
  description: 'tablespoons',
  descriptionKey: 'tablespoons',
  unit1: 'tbsp',
  value1: VolumeUnit.tbsp,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
};

export const Config_Teaspoons: MeasureUnitConfig = {
  id: 'tsp',
  description: 'teaspoons',
  descriptionKey: 'teaspoons',
  unit1: 'tsp',
  value1: VolumeUnit.tsp,
  locales: ['GB', 'IE', 'US', 'CA', 'AU', 'MM', 'LR'],
};

export class MeasurementType {
  id: string;
  nominalUnit: number;
  minimum: number; // applied at nominal unit scale
  maximum: number; // applied at nominal unit scale
  maximumChars?: number; // maximum number of digits to allow (this includes the decimal point and the minus sign - so only use for positive whole numbers!)
  precision: number; // a 1 at the least significant digit place - applied at current user unit!
  valueLen?: 'XS' | 'S' | 'M' | 'L' | 'XXS'; /** uses a smaller input field: XS ~ 2 digits, no decimals; S ~ 3 digits; M ~ 5 digits; L ~ 7 digits */
  configs: MeasureUnitConfig[];
  automaticRoller?: boolean; // if true, automatically generate roller values using the minimum, maximum and precision
  rollerLabel?: string; // label to use for roller. If none, don't automatically generate a label
  defaultValue?: number; // default value in nominal units when no current value exists
  forceDecimals?: boolean; // always show as many decimals as specified by the`precision` 
}

export const GlobalMeasurementTypes: MeasurementType[] = [
  {
    id: 'bodyweight_kg',
    nominalUnit: MassUnit.kg,
    minimum: 30,
    maximum: 600,
    precision: 0.1,
    valueLen: 'S',
    configs: [Config_Kilograms, Config_Pounds, Config_StonesPounds],
    automaticRoller: true,
    rollerLabel: 'weight',
    defaultValue: 70, // 70 kg is a reasonable default weight
    forceDecimals: true,
  },
  {
    id: 'bodyheight_cm',
    nominalUnit: LengthUnit.cm,
    minimum: 100,
    maximum: 300,
    precision: 1,
    valueLen: 'S',
    configs: [Config_Centimeters, Config_FeetInches],
    automaticRoller: true,
    rollerLabel: 'height',
    defaultValue: 170, // 170 cm is a reasonable default height
  },
  {
    id: 'bodydimensions_cm',
    nominalUnit: LengthUnit.cm,
    minimum: 1,
    maximum: 300,
    precision: 0.1,
    valueLen: 'S',
    configs: [Config_Centimeters, Config_Inches],
    automaticRoller: true,
    defaultValue: 35, // 35 cm is a reasonable default for body measurements like waist
    forceDecimals: true,
  },
  {
    id: 'addedweight_kg',
    nominalUnit: MassUnit.kg,
    minimum: 0,
    maximum: 2000,
    precision: 0.001,
    valueLen: 'M',
    configs: [Config_Kilograms, Config_Pounds],
  },
  {
    id: 'foodweight_g',
    nominalUnit: MassUnit.g,
    minimum: 0,
    maximum: 10000,
    precision: 0.1,
    valueLen: 'M',
    configs: [Config_Grams, Config_Kilograms, Config_Milligrams, Config_Ounces, Config_Pounds],
  },
  {
    id: 'nutrient_g',
    nominalUnit: MassUnit.g,
    minimum: 0,
    maximum: 10000,
    precision: 0.001,
    valueLen: 'M',
    configs: [Config_Grams],
  },
  {
    id: 'nutrient_mg',
    nominalUnit: MassUnit.mg,
    minimum: 0,
    maximum: 10000,
    precision: 0.001,
    valueLen: 'M',
    configs: [Config_Milligrams],
  },
  {
    id: 'nutrient_ug',
    nominalUnit: MassUnit.ug,
    minimum: 0,
    maximum: 10000,
    precision: 0.001,
    valueLen: 'M',
    configs: [Config_Micrograms],
  },
  {
    id: 'nutrient_kcal',
    nominalUnit: EnergyUnit.kcal,
    minimum: 0,
    maximum: 10000,
    precision: 0.1,
    valueLen: 'M',
    configs: [Config_Kcals],
  },
  {
    id: 'nutrient_kJ',
    nominalUnit: EnergyUnit.kcal,
    minimum: 0,
    maximum: 10000,
    precision: 0.1,
    valueLen: 'M',
    configs: [Config_Kjoules],
  },
  {
    id: 'nutrient_IU',
    nominalUnit: 1,
    minimum: 0,
    maximum: 100000,
    precision: 1,
    valueLen: 'M',
    configs: [Config_IU],
  },
  {
    id: 'integer',
    nominalUnit: 1,
    minimum: 0,
    maximum: 99,
    maximumChars: 2,
    precision: 1,
    valueLen: 'XS',
    configs: [Config_Number],
  },
  {
    id: 'myo-reps',
    nominalUnit: 1,
    minimum: 0,
    maximum: 9,
    maximumChars: 1,
    precision: 1,
    valueLen: 'XXS',
    configs: [Config_Number],
  },
  {
    id: 'reps',
    nominalUnit: 1,
    minimum: 0,
    maximum: 99,
    maximumChars: 2,
    precision: 1,
    valueLen: 'XS',
    configs: [Config_Reps],
    automaticRoller: true,
    rollerLabel: 'reps',
  },
  {
    id: 'sets',
    nominalUnit: 1,
    minimum: 0,
    maximum: 20,
    precision: 1,
    valueLen: 'M', // allows decimals, so needs a bit more space than 'reps'
    configs: [Config_Sets],
    automaticRoller: true,
    rollerLabel: 'sets',
  },
  {
    id: 'number',
    nominalUnit: 1,
    minimum: 0,
    maximum: 99,
    maximumChars: 2,
    precision: 1,
    valueLen: 'XS',
    configs: [Config_Number],
  },
  {
    id: 'intensity_pc',
    nominalUnit: 1,
    minimum: 1,
    maximum: 100,
    precision: 1,
    configs: [Config_Pc],
  },
  {
    id: 'bodyfat_pc',
    nominalUnit: 1,
    minimum: 1,
    maximum: 80,
    precision: 0.1,
    valueLen: 'S',
    configs: [Config_Pc],
    defaultValue: 20, // 20% is a reasonable default body fat percentage
  },
  {
    id: 'skinfold_mm',
    nominalUnit: LengthUnit.mm,
    minimum: 1,
    maximum: 100,
    precision: 0.1,
    valueLen: 'S',
    configs: [Config_Millimeters],
    automaticRoller: true,
    defaultValue: 10, // 10 mm is a reasonable default skinfold measurement
    forceDecimals: true,
  },
  {
    id: 'relative_factor',
    nominalUnit: 1,
    minimum: 0,
    maximum: 1000,
    precision: 0.00001,
    valueLen: 'L',
    configs: [Config_Number],
  },
  {
    id: 'food_fraction',
    nominalUnit: 1,
    valueLen: 'S',
    minimum: 0,
    maximum: 100,
    precision: 0.01,
    configs: [Config_Fraction],
  },
  {
    id: 'fraction',
    nominalUnit: 1,
    valueLen: 'M',
    minimum: 0,
    maximum: 1000,
    precision: 0.00001,
    configs: [Config_Fraction],
  },
  {
    id: 'food_volume',
    nominalUnit: 1,
    valueLen: 'M',
    minimum: 0,
    maximum: 2000,
    precision: 0.001,
    configs: [Config_Milliliters, Config_FluidOunces, Config_Quarts, Config_Pints, Config_Cups, Config_Tablespoons, Config_Teaspoons],
  },
  {
    id: 'percent',
    nominalUnit: 1,
    valueLen: 'M',
    minimum: 0,
    maximum: 100,
    precision: 0.01,
    configs: [Config_Pc],
  }
];

// list of all configs used by all measurement types
export const GlobalMeasurementConfigs: MeasureUnitConfig[] = GlobalMeasurementTypes.reduce((acc, curr) => acc.concat(curr.configs), []);